import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Constants from '../../../assests/Constants/constants';
import { styled } from '@mui/system';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import AddAccountCardModal from '../../Widgets/AddAccountCardModal/AddAccountCardModal';
import { useTranslation } from 'react-i18next';

import {
    Modal,
    Portal,
    TextField,
    Autocomplete,
    IconButton,
    Popper,
    Button,
    Tooltip,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import { getVatByCaseType } from '../../../Utils/GetVatValue';
import { ToastContainer, toast } from "react-toastify";
import { useDropzone } from 'react-dropzone';
import ClearIcon from '@mui/icons-material/Clear';
import ForeignCurrencyService from '../../../Services/foreignCurrency.service';
import ClientService from '../../../Services/client.service';
import ProjectsService from '../../../Services/projects.service';
import ProjectModal from '../../Projects/ProjectModal.jsx';
import AutocompleteSelectFilter from '../../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import styles from './CreateDocumentModal.module.scss';

function getAssignedProjectIdFromItem(item) {
    if (!item) return '';
    const p = item?.projects?.[0] ?? item?.segmentations?.[0];
    if (p) {
        const id = p.projectId ?? p.ProjectId ?? p.id ?? p.Id;
        if (id != null && id !== '') return String(id);
    }
    const segId =
        item.segmentationId ??
        item.SegmentationId ??
        item.segmentationProjectId ??
        item.projectId;
    if (segId != null && segId !== '') return String(segId);
    return '';
}

function normalizeSegmentationProjectRow(p) {
    const rawId = p?.id ?? p?.Id ?? p?.projectId ?? p?.ProjectId;
    const id = rawId != null && rawId !== '' ? String(rawId) : '';
    return {
        ...p,
        id,
        code: p?.code ?? p?.Code ?? '',
        name: p?.name ?? p?.Name ?? '',
    };
}

const StyledListItem = styled('li')({
    '&:hover': {
        backgroundColor: '#F5F5F6', // Hover color
        cursor: 'pointer', // Changes the cursor to pointer
    },
});

const ComboPopper = React.forwardRef(({ direction = 'rtl', ...props }, ref) => (
    <Popper
        {...props}
        ref={ref}
        dir={direction}
        className={`${props.className || ''} ${styles.comboPopper}`}
        placement="bottom-start"
    />
));
ComboPopper.displayName = 'ComboPopper';

const DropZoneContainer = styled('div')(({ isDragActive }) => ({
    // border: '2px dashed #ccc',
    width: '100%',
    textAlign: 'center',
    cursor: 'pointer',
    background: isDragActive ? '#e0e0e0' : 'transparent',
    transition: 'background 0.3s ease-in-out',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    position: 'relative',
}));

const DragOverlayText = styled('div')({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    padding: '10px 20px',
    borderRadius: '5px',
    display: 'none',
});
const FileList = styled('div')({
    marginTop: '10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});
const FileItem = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#f5f5f5',
    padding: '5px 10px',
    borderRadius: '5px',
    width: '100%',
    maxWidth: '300px',
    marginBottom: '5px',
});
const CreateDocumentModal = ({
    modalVisible,
    closeCreateModal,
    autoFilling,
    setAutoFilling,
    addNewAccountCardDocument,
    setAddNewAccountCardDocument,
    accountCardOptions,
    sortCodeIdOptions,
    creditAccountOptions,
    debitAccountOptions,
    type,
    formData,
    setFormData,
    handleSave,
    allFieldsFilled,
    documentDate,
    setDocumentDate,
    documentVat,
    setDocumentVat,
    handleDocumentUpload,
    documents,
    errorMessage,
    okMessage,
    client,
    token,
    setLoading,
    palCodes,
    fetchEnums,
    setDocuments,
    sortDate = null, // Add sortDate prop
    isClosedReportingMonth = false,
}) => {
    const { i18n, t } = useTranslation();

    const showProjectAssignRow = Boolean(client?.caseId && token && client?.managesProject);
    const [segmentationProjects, setSegmentationProjects] = useState([]);
    const [projectModalOpen, setProjectModalOpen] = useState(false);
    const [projectCreateLoading, setProjectCreateLoading] = useState(false);
    const [projectModalPortalContainer] = useState(() => document.createElement('div'));

    const projectAutocompleteOptions = useMemo(
        () => [
            { value: '__ADD_NEW__', id: '__ADD_NEW__', label: t('AddProject'), kind: 'add' },
            { value: '', id: '', label: t('Clear'), kind: 'none' },
            ...segmentationProjects.map((p) => ({
                value: String(p.id ?? ''),
                id: String(p.id ?? ''),
                label: `${p.code} — ${p.name}`,
                kind: 'project',
            })),
        ],
        [segmentationProjects, t]
    );

    const assignedProjectIdStr = useMemo(
        () => getAssignedProjectIdFromItem(formData),
        [formData]
    );

    const selectedProjectAutocompleteValue = useMemo(() => {
        if (!assignedProjectIdStr) return null;
        const matchedOption = projectAutocompleteOptions.find(
            (o) => o.kind === 'project' && String(o.value) === String(assignedProjectIdStr)
        );
        if (matchedOption) return matchedOption;

        const code = formData.segmentationCode ?? formData.SegmentationCode ?? '';
        const name = formData.segmentationName ?? formData.SegmentationName ?? '';
        if (!code && !name) return null;

        return {
            value: String(assignedProjectIdStr),
            id: String(assignedProjectIdStr),
            label: `${code} — ${name}`.replace(/^ — | — $/g, '').trim() || String(assignedProjectIdStr),
            kind: 'project',
        };
    }, [
        assignedProjectIdStr,
        projectAutocompleteOptions,
        formData.segmentationCode,
        formData.SegmentationCode,
        formData.segmentationName,
        formData.SegmentationName,
    ]);

    const fetchSegmentationProjects = useCallback(async () => {
        if (!client?.caseId || !token) {
            setSegmentationProjects([]);
            return;
        }
        try {
            const res = await ProjectsService.getProjects(client.caseId, token);
            if (!res.ok) return;
            const data = await res.json();
            const list = (Array.isArray(data) ? data : [])
                .map(normalizeSegmentationProjectRow)
                .filter((p) => p.id);
            setSegmentationProjects(list);
        } catch (error) {
            console.error('Error fetching projects:', error);
        }
    }, [client?.caseId, token]);

    useEffect(() => {
        if (!modalVisible || !showProjectAssignRow) return;
        void fetchSegmentationProjects();
    }, [modalVisible, showProjectAssignRow, fetchSegmentationProjects]);

    useEffect(() => {
        if (!projectModalOpen) return undefined;
        document.body.appendChild(projectModalPortalContainer);
        return () => {
            try {
                document.body.removeChild(projectModalPortalContainer);
            } catch {
                // ignore
            }
        };
    }, [projectModalOpen, projectModalPortalContainer]);

    const applyProjectToFormData = useCallback((newSegmentationId, optionalProjectMeta = null) => {
        const newId = newSegmentationId ? String(newSegmentationId) : '';
        const meta =
            optionalProjectMeta ||
            segmentationProjects.find((p) => String(p.id) === newId);
        setFormData((prev) => ({
            ...prev,
            projects: newId
                ? [{ projectId: newId, code: meta?.code ?? '', name: meta?.name ?? '' }]
                : [],
            segmentationId: newId || null,
            SegmentationId: newId || null,
            segmentationCode: newId ? meta?.code ?? '' : null,
            segmentationName: newId ? meta?.name ?? '' : null,
        }));
    }, [segmentationProjects, setFormData]);

    const handleProjectAutocompleteChange = (_event, newValue) => {
        if (newValue?.kind === 'add' || newValue?.value === '__ADD_NEW__' || newValue?.id === '__ADD_NEW__') {
            setProjectModalOpen(true);
            return;
        }
        if (!newValue || newValue.kind === 'none' || newValue.value === '' || newValue.id === '') {
            applyProjectToFormData('');
            return;
        }
        applyProjectToFormData(String(newValue.value ?? newValue.id));
    };

    const handleProjectModalClose = () => {
        setProjectModalOpen(false);
    };

    const handleProjectModalSave = async (projectInput) => {
        if (!client?.caseId || !token) return false;
        const drafts = Array.isArray(projectInput) ? projectInput : [projectInput];
        const normalized = drafts.map((d) => ({
            code: String(d?.code ?? '').trim(),
            name: String(d?.name ?? '').trim(),
        }));
        if (normalized.some((d) => !d.code || !d.name)) return false;

        setProjectCreateLoading(true);
        try {
            let lastCreated = null;
            for (const draft of normalized) {
                const res = await ProjectsService.createProject(
                    { CaseId: client.caseId, Code: draft.code, Name: draft.name },
                    token
                );
                if (!res.ok) {
                    const msg = await res.text();
                    toast.error(msg || t('ErrorCreatingProject'));
                    await fetchSegmentationProjects();
                    return false;
                }
                lastCreated = normalizeSegmentationProjectRow(await res.json());
            }
            toast.success(`${t('Add')} ${t('Projects')}`);
            await fetchSegmentationProjects();
            setProjectModalOpen(false);
            if (lastCreated?.id) {
                applyProjectToFormData(String(lastCreated.id), {
                    code: lastCreated.code,
                    name: lastCreated.name,
                });
            }
            return true;
        } catch (e) {
            console.error(e);
            toast.error(t('ErrorCreatingProject'));
            return false;
        } finally {
            setProjectCreateLoading(false);
        }
    };

    const [creditDebitSearchTerm, setCreditDebitSearchTerm] = useState('');
    const [newAccountCard, setNewAccountCard] = useState({ accountCodeName: '', accountCodeId: '' });
    const [isEditAccountCard, setIsEditAccountCard] = useState(false);
    const [isAddAccountCard, setIsAddAccountCard] = useState(false);
    const [editedAddAccountCard, setEditedAddAccountCard] = useState(false);
    const [palCode, setPalCode] = useState('');
    const [newCardId, setNewCardId] = useState(null);
    const [addAccountCardType, setAddAccountCardType] = useState('main');
    const [cardIdOption, setCardIdOption] = useState(-1);
    const [isDragging, setIsDragging] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [FCRate, setFCRate] = useState(1);
    const [isLoadingCurrencyRate, setIsLoadingCurrencyRate] = useState(false);
    const [manualRateInput, setManualRateInput] = useState(formData.exchangeRate ?? '');
    const lastManualRateRef = useRef(null);
    const userClearedManualRateRef = useRef(false);

    // Default text direction
    const [direction, setDirection] = useState('rtl');
    const [reportingMonthDatePickerKey, setReportingMonthDatePickerKey] = useState(0);
    useEffect(() => {
        if (i18n.language === 'he' || i18n.language === 'ar') {
            setDirection('rtl');
        } else {
            setDirection('ltr');
        }
    }, [i18n.language]);

    const exchangeRateType = formData.exchangeRateType ?? Constants.ExchangeRateType.Representative;
    const isManualRate = exchangeRateType === Constants.ExchangeRateType.Manual;

    useEffect(() => {
        if (userClearedManualRateRef.current) {
            setManualRateInput('');
            return;
        }
        const rate = formData.exchangeRate != null && formData.exchangeRate !== ''
            ? String(Number(formData.exchangeRate))
            : (lastManualRateRef.current ?? '');
        setManualRateInput(rate);
        if (rate) lastManualRateRef.current = rate;
    }, [formData.exchangeRateType, formData.exchangeRate]);

    useEffect(() => {
        async function fetchRate() {
            if (!token) {
                setIsLoadingCurrencyRate(false);
                return;
            }
            setIsLoadingCurrencyRate(true);
            try {
                const rate = await ForeignCurrencyService.GetIlsRateForDate(
                    formData.foreignCurrencyCode,
                    formData.documentDate,
                    token
                );
                setFCRate(rate);
                const amountRaw = formData.amount;
                if (rate != null && rate > 0 && amountRaw != null && amountRaw !== '' && !isNaN(Number(amountRaw))) {
                    const amountNum = Number(amountRaw);
                    const amountFixed = Number(amountNum.toFixed(2));
                    handleChange('amount', amountFixed);
                    const foreignAmount = (amountNum / rate).toFixed(2);
                    handleChange('foreignCurrencyAmount', foreignAmount);
                }
            } catch (err) {
                console.error(err);
                toast.error(t('CurrencyRateFetchError'));
            } finally {
                setIsLoadingCurrencyRate(false);
            }
        }
        if (
            formData.foreignCurrencyCode != null &&
            formData.foreignCurrencyCode !== '' &&
            formData.documentDate &&
            !isManualRate
        ) {
            fetchRate();
        } else {
            if (formData.foreignCurrencyCode === '' || formData.foreignCurrencyCode == null) {
                setFCRate(1);
            }
            setIsLoadingCurrencyRate(false);
        }
    }, [formData.foreignCurrencyCode, formData.documentDate, formData.exchangeRateType]);

    // When in Manual mode with no manual rate but Amount and ForeignCurrencyAmount present, keep lastManualRateRef for display/restore (display value is computed in render, not stored in manualRateInput)
    useEffect(() => {
        if (!isManualRate) return;
        if (formData.foreignCurrencyCode == null || formData.foreignCurrencyCode === '') return;
        if (formData.exchangeRate != null && formData.exchangeRate !== '') return;
        const amount = Number(formData.amount);
        const fc = Number(formData.foreignCurrencyAmount);
        if (!amount || !fc || isNaN(amount) || isNaN(fc) || amount <= 0 || fc <= 0) return;
        const calculated = (amount / fc).toFixed(2);
        lastManualRateRef.current = calculated;
    }, [formData.exchangeRateType, formData.exchangeRate, formData.amount, formData.foreignCurrencyAmount, formData.foreignCurrencyCode]);

    // Compute max Card ID
    useEffect(() => {
        if (accountCardOptions && accountCardOptions.length > 0) {
            const maxCardId = Math.max(
                ...accountCardOptions.slice(1).map((option) => parseInt(option.cardId, 10))
            );
            setCardIdOption(maxCardId + 1);
        }
    }, [accountCardOptions]);

    // Set initial month dates from sortDate
    useEffect(() => {
        if (sortDate) {
            setFormData((prev) => ({
                ...prev,
                registrationMonthDate: new Date(sortDate).toISOString(),
                reportingMonthDate: new Date(sortDate).toISOString(),
            }));
        }
    }, [sortDate, setFormData]);

    // Format account options
    const formattedAccountCardOptions = accountCardOptions.map((option) => ({
        value: option.cardId,
        label: option.accountCaseId
            ? `${option.name} - ${option.accountCaseId}`
            : `${option.name}`,
        name: option.name,
        accountCaseId: option.accountCaseId || '',
        cardId: option.cardId,
        value1: option.value1,
        isEditable: option.isEditable,
        palCode: option.palCode,
    }));
    // Format sort-code options
    const formattedSortCodeOptions = sortCodeIdOptions.map((option) => ({
        value: option.sortCodeId,
        label: `${option.sortCodeId} - ${option.sortCodeName}`,
        name: option.sortCodeName,
    }));

    // Basic input style (Xtram control look)
    const inputPropsStyle = {
        disableUnderline: true,
        className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
        sx: {
            height: '48px',
            padding: '0 8px',
            borderRadius: '8px',
            background: '#ffffff',
            border: '2px solid #e5e7f6',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
                borderColor: '#304fff',
            },
            '&:focus-within': {
                border: '2px solid #304fff',
                boxShadow: '0 0 0 2px rgba(48, 79, 255, 0.1)',
            },
            '& *': {
                boxSizing: 'border-box !important',
            },
        },
    };
    const inputStyle = {
        style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
    };

    const vatOptions = Constants.VATPercentage.map((option) => ({
        value: option,
        label: option === 16 ? t('AccountingTax') : `${option}%`,
    }));
    const lockedVat =
        client?.caseType && getVatByCaseType(client.caseType) !== undefined
            ? getVatByCaseType(client.caseType)
            : null;
    const currentVatValue =
        lockedVat != null ? lockedVat : parseInt(formData.vat, 10);
    const selectedVat =
        vatOptions.find((option) => option.value === currentVatValue) || vatOptions[0];

    const handleVatSelect = (_event, option) => {
        if (lockedVat != null) {
            toast.error(`${t('VAT')} ${client.caseType}`, { autoClose: 3000 });
            return;
        }
        handleChange('vat', parseInt(option?.value ?? 0, 10));
    };

    const currencyOptions = [
        { value: '', label: t('Clear') },
        ...Object.entries(Constants.ForeignCurrencyCode).map(([code, num]) => ({
            value: num,
            label: `${code}  ${Constants.ForeignCurrencySymbol[code] || ''}`.trim(),
        })),
    ];
    const selectedCurrency =
        formData.foreignCurrencyCode == null || formData.foreignCurrencyCode === ''
            ? null
            : currencyOptions.find(
                (option) => option.value === formData.foreignCurrencyCode,
            ) || null;

    const handleCurrencySelect = (_event, option) => {
        const raw = option?.value;
        const newCode = raw === '' || raw == null ? '' : Number(raw);
        handleChange('foreignCurrencyCode', newCode);
        if (newCode === '' || newCode === null || Number.isNaN(newCode)) {
            handleChange('amount', '');
            handleChange('foreignCurrencyAmount', '');
            handleChange('exchangeRate', '');
            setManualRateInput('');
            userClearedManualRateRef.current = true;
            lastManualRateRef.current = null;
        } else if (formData.amount) {
            recalcForeignAmount(formData.amount, newCode);
        } else if (formData.foreignCurrencyAmount) {
            recalcAmount(formData.foreignCurrencyAmount, newCode);
        }
    };

    const renderComboPopper = (props) => (
        <ComboPopper {...props} direction={direction} />
    );

    const renderAccountComboPopper = (props) => (
        <ComboPopper
            {...props}
            direction={direction}
            className={`${props.className || ''} ${styles.accountComboPopper}`}
        />
    );

    const renderAccountOption = (accountType) => (props, option) => {
        const isAddItem = option?.value1 === 'AddItem';
        const { key, className, ...optionProps } = props;
        return (
            <Tooltip
                title={
                    isAddItem
                        ? ''
                        : option.isEditable
                            ? t('RightClickToEdit')
                            : t('NotEditable')
                }
                placement="top"
                disableHoverListener={isAddItem}
            >
                <StyledListItem
                    key={key}
                    {...optionProps}
                    className={`${className || ''} ${isAddItem ? styles.addAccountOption : ''}`}
                    onContextMenu={(event) => {
                        if (isAddItem || !option.isEditable) {
                            event.preventDefault();
                            return;
                        }
                        event.preventDefault();
                        EditAccountCard(option, accountType);
                    }}
                >
                    {isAddItem
                        ? `+ ${option.name || t('AddAccountCard')}`
                        : `${option?.cardId || ''} - ${option.name}`}
                </StyledListItem>
            </Tooltip>
        );
    };

    const renderVatFilter = () => (
        <div className={styles.filterField}>
            <AutocompleteSelectFilter
                options={vatOptions}
                selectedValues={selectedVat}
                onChange={handleVatSelect}
                placeholder={t('VATPercentage')}
                isMultiple={false}
                direction={direction}
                portalMenu
            />
        </div>
    );

    const renderDescriptionField = () => (
        <div>
            <div className={styles.fieldTitle}>
                {t('enterDescription')}
                <span className={styles.required}>*</span>
            </div>
            <TextField
                fullWidth
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                variant="standard"
                InputProps={inputPropsStyle}
                inputProps={inputStyle}
                placeholder={t('enterDescription')}
            />
        </div>
    );

    const renderProjectsField = () => {
        if (!showProjectAssignRow) return null;
        return (
            <div className={styles.filterField}>
                <AutocompleteSelectFilter
                    options={projectAutocompleteOptions}
                    selectedValues={selectedProjectAutocompleteValue}
                    onChange={handleProjectAutocompleteChange}
                    label={t('Projects')}
                    placeholder={t('Projects')}
                    isMultiple={false}
                    isDisabled={isClosedReportingMonth || projectCreateLoading}
                    direction={direction}
                    portalMenu
                />
            </div>
        );
    };

    // Generic setter
    const handleChange = (field, value) => {
        if (value === null || value === undefined) return;
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleReportingMonthDateChange = async (date) => {
        if (!date) return;

        if (client?.caseId && token) {
            const newDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 10));
            const response = await ClientService.CheckIfMonthClosed(client.caseId, newDate, token);
            if (response.ok) {
                const isClosedReportingMonth = await response.json();
                if (isClosedReportingMonth) {
                    toast.info(t('MonthReportAlreadyClosedTryAgain'));
                    setReportingMonthDatePickerKey((prev) => prev + 1);
                    return;
                }
            }
        }

        handleChange('reportingMonthDate', date.toISOString());
    };

    const onDrop = useCallback((acceptedFiles) => {
        setIsDragging(false);

        if (acceptedFiles.length > 0) {
            // Convert to a proper array
            const newDocuments = [...documents, ...acceptedFiles];

            // Ensure setDocuments receives an array
            setDocuments(newDocuments);
            handleDocumentUpload({ target: { files: newDocuments } });
        }
    }, [handleDocumentUpload, documents, setDocuments]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDragEnter: () => setIsDragging(true),
        onDragLeave: () => setIsDragging(false),
    });

    // Clear all selected files
    const clearSelectedFiles = () => {
        setDocuments([]);
        handleDocumentUpload({ target: { files: [] } });
    };

    // Remove a single file
    const removeFile = (index) => {
        const updatedFiles = [...documents];
        updatedFiles.splice(index, 1);
        setDocuments(updatedFiles);
        handleDocumentUpload({ target: { files: updatedFiles } });
    };

    const recalcAmount = async (foreignCurrencyAmount, code = null, date = null) => {
        if (foreignCurrencyAmount == null || foreignCurrencyAmount === '') {
            return;
        }
        if (!code && formData.foreignCurrencyCode == null) {
            toast.info(t('ToCalculateAmountWithForeignCurrencyChooseDateAndCurrencyCode'));
            return;
        }
        const fcNum = Number(foreignCurrencyAmount);
        if (isNaN(fcNum) || fcNum <= 0) return;
        const isManual = (formData.exchangeRateType ?? Constants.ExchangeRateType.Representative) === Constants.ExchangeRateType.Manual;
        const manualRate = isManual ? Number(formData.exchangeRate || manualRateInput) : null;
        if (isManual) {
            if (manualRate > 0 && !isNaN(manualRate)) {
                const amount = Number((fcNum * manualRate).toFixed(2));
                handleChange('amount', amount);
            }
            return;
        }

        const fxCode = code ?? formData.foreignCurrencyCode;
        const fxDate = date ?? formData.documentDate;
        try {
            const rate = await ForeignCurrencyService.GetIlsRateForDate(fxCode, fxDate, token);
            const amount = Number((fcNum * rate).toFixed(2));
            handleChange('amount', amount);
        } catch (err) {
            console.error('Failed to recalc FC amount', err);
            toast.error(t('CurrencyRateFetchError'));
        }
    };

    const recalcForeignAmount = async (ilsAmount, code = null, date = null) => {
        if (ilsAmount == null || ilsAmount === '') return;
        if (!code && formData.foreignCurrencyCode == null) return;
        const amountNum = Number(ilsAmount);
        if (isNaN(amountNum) || amountNum <= 0) return;
        const isManual = (formData.exchangeRateType ?? Constants.ExchangeRateType.Representative) === Constants.ExchangeRateType.Manual;
        const manualRate = isManual ? Number(formData.exchangeRate || manualRateInput) : null;
        if (isManual) {
            if (manualRate > 0 && !isNaN(manualRate)) {
                const foreignAmount = Number((amountNum / manualRate).toFixed(2));

                handleChange('foreignCurrencyAmount', foreignAmount);
            }
            return;
        }
        if (!(date ?? formData.documentDate)) return;
        const fxCode = code ?? formData.foreignCurrencyCode;
        const fxDate = date ?? formData.documentDate;
        try {
            const rate = await ForeignCurrencyService.GetIlsRateForDate(fxCode, fxDate, token);
            const foreignAmount = Number((amountNum / rate).toFixed(2));
            handleChange('foreignCurrencyAmount', foreignAmount);
        } catch (err) {
            console.error('Failed to recalc ILS amount', err);
            toast.error(t('CurrencyRateFetchError'));
        }
    };

    const handleForeignAmountChange = (value) => {
        if (isManualRate) userClearedManualRateRef.current = false;
        handleChange('foreignCurrencyAmount', value);
        recalcAmount(value);
    };

    const handleAmountChange = (value) => {
        if (isManualRate) userClearedManualRateRef.current = false;
        handleChange('amount', value);
        if (formData.foreignCurrencyCode && value) {
            recalcForeignAmount(value);
        }
    };

    const handleManualRateChange = (value) => {
        handleChange('exchangeRate', value);
        if (value !== '' && !isNaN(Number(value)) && Number(value) > 0) {
            lastManualRateRef.current = value;
        }
        if (value === '' || isNaN(Number(value)) || Number(value) <= 0 || formData.amount == null || formData.amount === '') return;
        if (formData.foreignCurrencyCode == null || formData.foreignCurrencyCode === '') {
            toast.info(t('ToCalculateAmountWithForeignCurrencyChooseDateAndCurrencyCode'));
            return;
        }
        handleChange('foreignCurrencyAmount', (Number(formData.amount) / Number(value)).toFixed(2));
    };

    const getManualRateDisplayState = () => {
        const amountNum = Number(formData.amount);
        const fcNum = Number(formData.foreignCurrencyAmount);
        const hasForeignCode = formData.foreignCurrencyCode != null && formData.foreignCurrencyCode !== '';
        const hasValidAmountAndFc = hasForeignCode && amountNum > 0 && fcNum > 0 && !isNaN(amountNum) && !isNaN(fcNum);
        const calculatedManualRate = hasValidAmountAndFc ? (amountNum / fcNum).toFixed(2) : null;
        const manualRateDisplayValue = (manualRateInput !== '' && manualRateInput != null)
            ? manualRateInput
            : (userClearedManualRateRef.current ? '' : (calculatedManualRate ?? ''));
        const hasValidDisplayRate = manualRateDisplayValue !== '' && !isNaN(Number(manualRateDisplayValue)) && Number(manualRateDisplayValue) > 0;
        const isShowingCalculatedRate = (manualRateInput === '' || manualRateInput == null) && calculatedManualRate != null && !userClearedManualRateRef.current;
        return {
            amountNum,
            fcNum,
            hasForeignCode,
            hasValidAmountAndFc,
            calculatedManualRate,
            manualRateDisplayValue,
            hasValidDisplayRate,
            isShowingCalculatedRate,
        };
    };

    // Handle sort-code selection
    const handleSortCodeChange = (value) => {
        if (!value || !sortCodeIdOptions.some((option) => option.sortCodeId === value)) {
            // Set default values for the empty state
            setFormData((prev) => ({
                ...prev,
                vat: 100, // Default VAT
                sortCodeId: '',
            }));
            return; // Exit early since there's no valid sortCodeId
        }

        const selectedOption = sortCodeIdOptions.find((option) => option.sortCodeId === value);
        if (!selectedOption) return;

        const name = selectedOption.sortCodeName || '';

        // Extract the name without the parentheses
        const nameWithoutParentheses = name.replace(/\s*\(.*?\)\s*/, '').trim();

        // Check for a number within parentheses
        const match = name.match(/\((\d+)%?/);
        let customVat = match ? parseInt(match[1], 10) : null;

        const vat = Constants.SortCodesWith66Vat.includes(nameWithoutParentheses)
            ? Constants.Vat66
            : Constants.SortCodesWithZeroVat.includes(nameWithoutParentheses) ||
                selectedOption.sortCodeVatType === 0
                ? 0
                : Constants.SortCodesWith16Vat.includes(nameWithoutParentheses)
                    ? 16
                    : customVat !== null
                        ? customVat === Constants.Vat66
                            ? Constants.Vat66
                            : customVat
                        : 100;

        const sortCode = selectedOption;
        const newVat = sortCode?.vat ?? vat;

        const accountCard = accountCardOptions.find(
            (item) => item.cardId === (type === 'Incomes' ? sortCode.debitAccount : sortCode.creditAccount)
        );

        const debit = accountCardOptions.find(
            (item) => item.cardId === sortCode.debitAccount
        );

        const credit = accountCardOptions.find(
            (item) => item.cardId === sortCode.creditAccount
        );

        if (value === '1') { //hova
            setFormData((prev) => ({
                ...prev,
                vat: newVat,
                sortCodeId: value,
                creditAccount: null,
                debitAccount: debit ? debit.cardId : prev.debitAccount,
                accountCodeName: accountCard ? accountCard.name : prev.accountCodeName,
                accountCodeId: accountCard ? accountCard.cardId : prev.accountCodeId,
                accountCaseId: accountCard ? accountCard.accountCaseId : prev.accountCaseId,
            }));
            return;
        }
        if (value === '2') { //z5ot
            setFormData((prev) => ({
                ...prev,
                vat: newVat,
                sortCodeId: value,
                creditAccount: credit ? credit.cardId : prev.creditAccount,
                debitAccount: null,
                accountCodeName: accountCard ? accountCard.name : prev.accountCodeName,
                accountCodeId: accountCard ? accountCard.cardId : prev.accountCodeId,
                accountCaseId: accountCard ? accountCard.accountCaseId : prev.accountCaseId,
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            vat: newVat,
            sortCodeId: value,
            creditAccount: credit ? credit.cardId : prev.creditAccount,
            debitAccount: debit ? debit.cardId : prev.debitAccount,
            accountCodeName: accountCard ? accountCard.name : prev.accountCodeName,
            accountCodeId: accountCard ? accountCard.cardId : prev.accountCodeId,
            accountCaseId: accountCard ? accountCard.accountCaseId : prev.accountCaseId,
        }));
    };

    // Edit existing account card
    const EditAccountCard = (selectedItem, accountType) => {
        if (!selectedItem) {
            console.warn('No account card selected for editing.');
            return;
        }
        setPalCode(selectedItem.palCode);
        setNewAccountCard({
            accountCodeName: selectedItem.name || '',
            accountCodeId: selectedItem.cardId,
            accountCaseId: selectedItem.accountCaseId || '',
        });
        setIsEditAccountCard(true);
        setAddAccountCardType(accountType);
        setIsAddAccountCard(false);
        setEditedAddAccountCard(true);
    };

    // Update account fields in the form
    const updateAccountFields = (newValue, accountType) => {
        if (newValue?.value1 === 'AddItem') {
            handleNoOptionCreditDebitClick('', accountType);
            return;
        }
        
        const isIncomesDebit = type === 'Incomes' && accountType === 'debitAccount';
        const isExpensesCredit = type === 'Expenses' && accountType === 'creditAccount';
        
        const updatedFields = {
            accountCodeId: (isIncomesDebit || isExpensesCredit) ? (newValue ? newValue.cardId : '') : '',
            accountCodeName: (isIncomesDebit || isExpensesCredit) ? (newValue ? newValue.name : '') : '',
            accountCaseId: (isIncomesDebit || isExpensesCredit) ? (newValue ? newValue.accountCaseId : '') : '',
        };

        setFormData((prev) => ({
            ...prev,
            [accountType]: newValue ? newValue.cardId : '',
            ...((isIncomesDebit || isExpensesCredit) ? updatedFields : {}),
        }));
    };

    // After user adds new account card
    const handleChangeAfterAdd = (cardId, accountType) => {
        const newCard = { cardId, type: accountType };
        setNewCardId(newCard);
    };
    useEffect(() => {
        if (accountCardOptions && accountCardOptions.length > 0 && newCardId) {
            const selectedOption = accountCardOptions.find(
                (option) => option.cardId === newCardId.cardId
            );
            updateAccountFields(selectedOption, newCardId.type);
            setNewCardId(null);
        }
    }, [accountCardOptions, newCardId]);

    // Journal Entry logic
    const handleCreditDebitForJournalEntry = (newValue, accountType) => {
        if (!newValue) {
            setFormData((prev) => ({ ...prev, [accountType]: '' }));
            return;
        }
        if (newValue.value1 === 'AddItem') {
            setIsAddAccountCard(true);
            setAddAccountCardType(accountType);
            setNewAccountCard({
                accountCodeName: newValue.searchTerm || '',
                accountCodeId: cardIdOption,
            });
            setEditedAddAccountCard(true);
            setIsEditAccountCard(false);
        } else {
            updateAccountFields(newValue, accountType);
        }
    };
    const handleNoOptionCreditDebitClick = (searchTerm, accountType) => {
        const newOption = {
            ...formattedAccountCardOptions[0],
            searchTerm: searchTerm,
            value1: 'AddItem',
        };
        handleCreditDebitForJournalEntry(newOption, accountType);
    };

    // For main account code field
    const handleAccountCodeChange = (selectedOption) => {
        if (!selectedOption) return;
        if (selectedOption?.value1 === 'AddItem') {
            setFormData((prev) => ({
                ...prev,
                accountCodeId: cardIdOption,
                accountCaseId: '',
                accountCodeName: '',
                creditAccount: '',
                debitAccount: '',
            }));
            setAddNewAccountCardDocument(true);
        } else {
            const accountCard = accountCardOptions.find(
                (option) => option.cardId === selectedOption?.value
            );
            setFormData((prev) => ({
                ...prev,
                accountCodeId: selectedOption?.value || '',
                accountCaseId: selectedOption?.accountCaseId || '',
                accountCodeName: accountCard ? accountCard.name : '',
                creditAccount: type !== 'Incomes' ? selectedOption.value : '',
                debitAccount: type === 'Incomes' ? selectedOption.value : '',
            }));
        }
    };
    const handleCloseAddNewAccountCardDocument = () => {
        setFormData((prev) => ({
            ...prev,
            accountCodeId: '',
            accountCaseId: '',
            accountCodeName: '',
            creditAccountOption: '',
            debitAccountOption: '',
        }));
        setAddNewAccountCardDocument(false);
    };

    return (
        <>
        <Modal
            open={modalVisible}
            onClose={closeCreateModal}
            disableEnforceFocus={projectModalOpen}
            disableAutoFocus={projectModalOpen}
        >
            <div className={styles.overlay}>
                <div className={`${styles.modal} ${autoFilling ? styles.modalAuto : ''} ${direction === 'ltr' ? styles.ltr : styles.rtl}`}>
                    <div className={styles.scroll}>
                        <div className={styles.header}>
                            <h2>{t('newDocument')}</h2>
                            <div className={styles.modeToggle}>
                                <button
                                    type="button"
                                    className={`${styles.modeBtn} ${autoFilling ? styles.modeBtnActive : ''}`}
                                    onClick={() => setAutoFilling(true)}
                                >
                                    {t('Automatic Filling')}
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.modeBtn} ${!autoFilling ? styles.modeBtnActive : ''}`}
                                    onClick={() => setAutoFilling(false)}
                                >
                                    {t('Manual Filling')}
                                </button>
                            </div>
                        </div>

                        {/* MANUAL-FILLING FIELDS */}
                        {!autoFilling && (
                            <div className={styles.formBody}>
                                {/* Row 1: Sort Code ID / Month Date Fields Combined */}
                                <div className={styles.formRow}>
                                    <div className={styles.field}>
                                        <div className={styles.fieldTitle}>
                                            {t('SortCode Id')}
                                            <span className={styles.required}>*</span>
                                        </div>
                                        <Autocomplete
                                            options={formattedSortCodeOptions}
                                            getOptionLabel={(option) => option.label}
                                            value={
                                                formattedSortCodeOptions.find(
                                                    (option) => option.value === formData.sortCodeId
                                                ) || null
                                            }
                                            onChange={(e, newValue) =>
                                                handleSortCodeChange(newValue ? newValue.value : '')
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="standard"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        ...inputPropsStyle,
                                                    }}
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        ...inputStyle,
                                                    }}
                                                    placeholder={t("SortCode Id")}
                                                />
                                            )}
                                            PopperComponent={renderComboPopper}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <div className={styles.monthPickersRow}>
                                            <div className={styles.monthPickerItem}>
                                                <div className={styles.fieldTitle}>
                                                    {t('MonthReportDate')}
                                                </div>
                                                <CustomMonthDatePicker
                                                    key={reportingMonthDatePickerKey}
                                                    onDateChange={(date) => {
                                                        if (date) handleReportingMonthDateChange(date);
                                                    }}
                                                    date={formData.reportingMonthDate ? new Date(formData.reportingMonthDate) : null}
                                                    direction={direction}
                                                />
                                            </div>
                                            <div className={styles.monthPickerItem}>
                                                <div className={styles.fieldTitle}>
                                                    {t('RegistrationMonthDate')}
                                                </div>
                                                <CustomMonthDatePicker
                                                    onDateChange={(date) => {
                                                        if (date) {
                                                            const iso = date.toISOString();
                                                            handleChange('registrationMonthDate', iso);
                                                        }
                                                    }}
                                                    date={formData.registrationMonthDate ? new Date(formData.registrationMonthDate) : null}
                                                    direction={direction}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Row 2: Debit Account / Document Date */}
                                <div className={styles.formRow}>
                                    <div className={styles.field}>
                                        <div className={styles.fieldTitle}>
                                            {t('Debit Account')}
                                            <span className={styles.required}>*</span>
                                        </div>
                                        <Autocomplete
                                            options={formattedAccountCardOptions}
                                            getOptionLabel={(option) =>
                                                option?.value1 === 'AddItem'
                                                    ? (option.name || t('AddAccountCard'))
                                                    : `${option.cardId} - ${option.name}`
                                            }
                                            value={
                                                formattedAccountCardOptions.find(
                                                    (option) =>
                                                        option.cardId === formData.debitAccount
                                                ) || null
                                            }
                                            onChange={(event, newValue) => {
                                                updateAccountFields(newValue, 'debitAccount');
                                            }}
                                            onInputChange={(event, inputValue) => {
                                                setCreditDebitSearchTerm(inputValue);
                                            }}
                                            noOptionsText={
                                                <Button
                                                    onClick={() =>
                                                        handleNoOptionCreditDebitClick(
                                                            creditDebitSearchTerm,
                                                            'debitAccount'
                                                        )
                                                    }
                                                    variant="contained"
                                                    color="primary"
                                                >
                                                    {t('AddAccountCard')} "{creditDebitSearchTerm}"
                                                </Button>
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder={t('Debit Account')}
                                                    variant="standard"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        ...inputPropsStyle,
                                                    }}
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        ...inputStyle,
                                                    }}
                                                />
                                            )}
                                            renderOption={renderAccountOption('debitAccount')}
                                            PopperComponent={renderAccountComboPopper}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <div className={styles.fieldTitle}>
                                            {t('Document Date')}
                                            <span className={styles.required}>*</span>
                                        </div>
                                        <CustomDatePicker
                                            date={
                                                formData.documentDate
                                                    ? formData.documentDate
                                                    : null
                                            }
                                            onDateChange={(date) => {
                                                if (date) {
                                                    const iso = date.toISOString();
                                                    handleChange('documentDate', iso);
                                                    if (formData.foreignCurrencyAmount && formData.foreignCurrencyCode) {
                                                    recalcAmount(
                                                        formData.foreignCurrencyAmount,
                                                        formData.foreignCurrencyCode,
                                                        iso
                                                    );
                                                    }
                                                }
                                            }}
                                            height="48px"
                                            smartParseOnEnter
                                            textFieldClassName={styles.styleInput}
                                            inputSx={{
                                                background: '#ffffff',
                                                border: 'none',
                                                height: '48px',
                                                padding: '0 8px',
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Row 3: Credit Account / VAT Percentage */}
                                <div className={styles.formRow}>
                                    <div className={styles.field}>
                                        <div className={styles.fieldTitle}>
                                            {t('Credit Account')}
                                            <span className={styles.required}>*</span>
                                        </div>
                                        <Autocomplete
                                            options={formattedAccountCardOptions}
                                            getOptionLabel={(option) =>
                                                option?.value1 === 'AddItem'
                                                    ? (option.name || t('AddAccountCard'))
                                                    : `${option.cardId} - ${option.name}`
                                            }
                                            value={
                                                formattedAccountCardOptions.find(
                                                    (option) =>
                                                        option.cardId === formData.creditAccount
                                                ) || null
                                            }
                                            onChange={(event, newValue) => {
                                                updateAccountFields(newValue, 'creditAccount');
                                            }}
                                            onInputChange={(event, inputValue) => {
                                                setCreditDebitSearchTerm(inputValue);
                                            }}
                                            noOptionsText={
                                                <Button
                                                    onClick={() =>
                                                        handleNoOptionCreditDebitClick(
                                                            creditDebitSearchTerm,
                                                            'creditAccount'
                                                        )
                                                    }
                                                    variant="contained"
                                                    color="primary"
                                                >
                                                    {t('AddAccountCard')} "{creditDebitSearchTerm}"
                                                </Button>
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder={t('Credit Account')}
                                                    variant="standard"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        ...inputPropsStyle,
                                                    }}
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        ...inputStyle,
                                                    }}
                                                />
                                            )}
                                            renderOption={renderAccountOption('creditAccount')}
                                            PopperComponent={renderAccountComboPopper}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <div className={styles.fieldTitle}>
                                            {t('VATPercentage')}
                                            <span className={styles.required}>*</span>
                                        </div>
                                        {renderVatFilter()}
                                    </div>
                                </div>

                                {/* Row 4: Company ID */}
                                <div className={styles.formRow}>
                                    <div className={styles.field}>
                                        <div className={styles.fieldTitle}>
                                            {t('CompanyId')}
                                        </div>
                                        <TextField
                                            fullWidth
                                            value={formData.accountCaseId || ''}
                                            onChange={(e) =>
                                                handleChange('accountCaseId', e.target.value)
                                            }
                                            variant="standard"
                                            InputProps={inputPropsStyle}
                                            inputProps={inputStyle}
                                            placeholder={t('CompanyId')}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <div className={styles.fieldTitle}>
                                            {t('Amount')}
                                            <span className={styles.required}>*</span>
                                        </div>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            value={formData.amount || ''}
                                            onChange={(e) => handleAmountChange(e.target.value)}
                                            variant="standard"
                                            InputProps={{
                                                ...inputPropsStyle,
                                                sx: {
                                                    ...inputPropsStyle.sx,
                                                    border: (!formData.amount || formData.amount === '' || isNaN(Number(formData.amount)) || Number(formData.amount) === 0)
                                                        ? '2px solid #c62828'
                                                        : '2px solid #e5e7f6',
                                                },
                                            }}
                                            inputProps={inputStyle}
                                            placeholder={t('Amount')}
                                        />
                                    </div>
                                </div>

                                {/* Row 5: Reference / Amount */}
                                <div className={styles.formRow}>
                                    <div className={styles.field}>
                                        <div className={styles.fieldTitle}>
                                            {t('Reference')}
                                        </div>
                                        <TextField
                                            fullWidth
                                            value={formData.reference || ''}
                                            onChange={(e) =>
                                                handleChange('reference', e.target.value)
                                            }
                                            variant="standard"
                                            InputProps={inputPropsStyle}
                                            inputProps={inputStyle}
                                            placeholder={t('Reference')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <div className={styles.fieldTitle}>
                                            {t('ConfirmationNumber')}
                                        </div>
                                        <TextField
                                            fullWidth
                                            value={formData.confirmationNumber || ''}
                                            onChange={(e) =>
                                                handleChange('confirmationNumber', e.target.value)
                                            }
                                            variant="standard"
                                            InputProps={inputPropsStyle}
                                            inputProps={inputStyle}
                                            placeholder={t('ConfirmationNumber')}
                                        />
                                    </div>
                                </div>

                                {/* Row 6: Reference2 / ConfirmationNumber */}
                                <div className={styles.formRow}>
                                    <div
                                        className={styles.field}
                                        style={
                                            isManualRate
                                                ? { display: 'flex', flexDirection: 'column', alignSelf: 'flex-start' }
                                                : undefined
                                        }
                                    >
                                        <div className={styles.fieldTitle}>
                                            {t('Reference2')}
                                        </div>
                                        <TextField
                                            fullWidth
                                            value={formData.reference2 || ''}
                                            onChange={(e) =>
                                                handleChange('reference2', e.target.value)
                                            }
                                            variant="standard"
                                            InputProps={inputPropsStyle}
                                            inputProps={inputStyle}
                                            placeholder={t('Reference2')}
                                        />
                                        {isManualRate && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 16,
                                                    marginTop: 16,
                                                    width: '100%',
                                                }}
                                            >
                                                {renderDescriptionField()}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.field}>
                                        <div className={styles.fieldTitle} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            {t('Currency')}
                                            <div className={styles.rateToggle}>
                                                <div
                                                    className={`${styles.rateToggleBtn}${exchangeRateType !== Constants.ExchangeRateType.Manual ? ` ${styles.rateToggleBtnActive}` : ''}`}
                                                    onClick={() => {
                                                        if (manualRateInput !== '' && !isNaN(Number(manualRateInput)) && Number(manualRateInput) > 0) {
                                                            lastManualRateRef.current = manualRateInput;
                                                        }
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            exchangeRateType: Constants.ExchangeRateType.Representative,
                                                            exchangeRate: null,
                                                        }));
                                                        setManualRateInput('');
                                                    }}
                                                >
                                                    {t('RepresentativeRate')}
                                                </div>
                                                <div
                                                    className={`${styles.rateToggleBtn}${exchangeRateType === Constants.ExchangeRateType.Manual ? ` ${styles.rateToggleBtnActive}` : ''}`}
                                                    onClick={() => {
                                                        let restored = formData.exchangeRate != null && formData.exchangeRate !== ''
                                                            ? String(Number(formData.exchangeRate))
                                                            : (lastManualRateRef.current ?? '');
                                                        if (!restored) {
                                                            const amount = Number(formData.amount);
                                                            const fc = Number(formData.foreignCurrencyAmount);
                                                            if (amount > 0 && fc > 0 && !isNaN(amount) && !isNaN(fc)) {
                                                                restored = (amount / fc).toFixed(2);
                                                            }
                                                        }
                                                        setManualRateInput(restored);
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            exchangeRateType: Constants.ExchangeRateType.Manual,
                                                            exchangeRate: restored || null,
                                                        }));
                                                    }}
                                                >
                                                    {t('ManualRate')}
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`${styles.filterField}${(exchangeRateType === Constants.ExchangeRateType.Manual && (formData.foreignCurrencyCode == null || formData.foreignCurrencyCode === '')) ? ` ${styles.filterFieldRequired}` : ''}`}>
                                            <AutocompleteSelectFilter
                                                options={currencyOptions}
                                                selectedValues={selectedCurrency}
                                                onChange={handleCurrencySelect}
                                                placeholder={t('Currency')}
                                                isMultiple={false}
                                                direction={direction}
                                                portalMenu
                                            />
                                        </div>
                                        {isManualRate && (() => {
                                            const state = getManualRateDisplayState();
                                            const helperText = state.isShowingCalculatedRate
                                                ? t('CalculatedValue')
                                                : !state.hasValidAmountAndFc
                                                    ? t('ToCalculateAmountWithForeignCurrencyChooseDateAndCurrencyCode')
                                                    : undefined;
                                            return (
                                            <div className={styles.manualRateField}>
                                                <div className={styles.fieldTitle}>{t('ManualExchangeRate')}</div>
                                                <TextField
                                                    fullWidth
                                                    placeholder={t('ManualExchangeRate')}
                                                    value={state.manualRateDisplayValue}
                                                    helperText={helperText}
                                                    onFocus={() => {
                                                        if (formData.exchangeRate != null && formData.exchangeRate !== '') {
                                                            setManualRateInput(String(Number(formData.exchangeRate)));
                                                        } else {
                                                            setManualRateInput(manualRateInput || lastManualRateRef.current || state.calculatedManualRate || '');
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        const val = e.target.value;
                                                        if (val !== '' && !isNaN(val) && Number(val) > 0) {
                                                            const formatted = Number(val).toFixed(2);
                                                            setManualRateInput(formatted);
                                                            handleManualRateChange(formatted);
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        userClearedManualRateRef.current = (v === '' || v == null);
                                                        setManualRateInput(v);
                                                        handleManualRateChange(v);
                                                    }}
                                                    variant="standard"
                                                    type="number"
                                                    InputProps={{
                                                        ...inputPropsStyle,
                                                        sx: {
                                                            ...inputPropsStyle.sx,
                                                            border: (exchangeRateType === Constants.ExchangeRateType.Manual && !state.hasValidDisplayRate)
                                                                ? '2px solid #c62828'
                                                                : '2px solid #e5e7f6',
                                                        },
                                                    }}
                                                    inputProps={inputStyle}
                                                />
                                            </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* Row 7: Description or Projects / Currency Amount */}
                                <div className={styles.formRow}>
                                    {isManualRate ? (
                                        showProjectAssignRow ? (
                                            <div className={styles.field}>
                                                {renderProjectsField()}
                                            </div>
                                        ) : (
                                            <div className={styles.field} aria-hidden="true" />
                                        )
                                    ) : (
                                        <div className={styles.field}>
                                            {renderDescriptionField()}
                                        </div>
                                    )}
                                    <div className={styles.field}>
                                        <div className={styles.fieldTitle}>
                                            {t('CurrencyAmount')}
                                            <span className={styles.required}>*</span>
                                        </div>
                                        <TextField
                                            fullWidth
                                            placeholder={t('CurrencyAmount')}
                                            value={formData.foreignCurrencyAmount || ''}
                                            onChange={(e) =>
                                                handleForeignAmountChange(e.target.value)
                                            }
                                            variant="standard"
                                            type="number"
                                            InputProps={{
                                                ...inputPropsStyle,
                                                endAdornment: isLoadingCurrencyRate ? (
                                                    <InputAdornment position="end">
                                                        <CircularProgress size={22} />
                                                    </InputAdornment>
                                                ) : null,
                                            }}
                                            inputProps={inputStyle}
                                            required
                                            disabled={isLoadingCurrencyRate}
                                        />
                                    </div>

                                </div>


                                {!isManualRate && showProjectAssignRow && (
                                    <div className={styles.formRow}>
                                        <div className={styles.field}>
                                            {renderProjectsField()}
                                        </div>
                                        <div className={styles.field} aria-hidden="true" />
                                    </div>
                                )}
                        </div>
                        )}
                        {/* Always-shown (auto or manual) */}
                        <div className={styles.formBody}>
                            {/* VAT changed to a Select-based field */}
                            {autoFilling &&
                                <div className={styles.field}>
                                    <div className={styles.fieldTitle}>
                                        {t('VATPercentage')}
                                        <span className={styles.required}>*</span>
                                    </div>
                                    {renderVatFilter()}
                                </div>
                            }

                            {/* Month Date Fields for Automatic Filling */}
                            {autoFilling && (
                                <div className={styles.field}>
                                    <div className={styles.monthPickersRow}>
                                        <div className={styles.monthPickerItem}>
                                            <div className={styles.fieldTitle}>
                                                {t('MonthReportDate')}
                                            </div>
                                            <CustomMonthDatePicker
                                                key={reportingMonthDatePickerKey}
                                                onDateChange={(date) => {
                                                    if (date) handleReportingMonthDateChange(date);
                                                }}
                                                date={formData.reportingMonthDate ? new Date(formData.reportingMonthDate) : null}
                                                direction={direction}
                                            />
                                        </div>
                                        <div className={styles.monthPickerItem}>
                                            <div className={styles.fieldTitle}>
                                                {t('RegistrationMonthDate')}
                                            </div>
                                            <CustomMonthDatePicker
                                                onDateChange={(date) => {
                                                    if (date) {
                                                        const iso = date.toISOString();
                                                        handleChange('registrationMonthDate', iso);
                                                    }
                                                }}
                                                date={formData.registrationMonthDate ? new Date(formData.registrationMonthDate) : null}
                                                direction={direction}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {autoFilling && showProjectAssignRow && (
                                <div className={styles.field} style={{ marginTop: '8px' }}>
                                    {renderProjectsField()}
                                </div>
                            )}

                            <div className={styles.formBody}>
                                {/* Enhanced Drag and Drop File Upload */}
                                <div
                                    className={`${styles.uploadZone} ${autoFilling ? styles.uploadZoneActive : ''}`}
                                    {...getRootProps()}
                                    style={{
                                        border: '2px dashed #E0E0E0',
                                        borderRadius: '12px',
                                        padding: '18px',
                                        textAlign: 'center',
                                        background: isDragActive ? '#F0F8FF' : '#FAFAFA',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        minHeight: '80px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative'
                                    }}
                                >
                                    <DropZoneContainer isDragActive={isDragActive}>
                                        <input {...getInputProps()} />

                                        {/* Show message when dragging */}
                                        {isDragging && (
                                            <DragOverlayText style={{ display: 'block' }}>
                                                {t('DropFilesHere')}
                                            </DragOverlayText>
                                        )}

                                        {!isDragging && (
                                            <label className={styles.fieldTitle} style={{ cursor: 'pointer' }}>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="upload-icon"
                                                    viewBox="0 0 58 58"
                                                    fill="none"
                                                    style={{ width: '48px', height: '48px', marginBottom: '12px' }}
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M29 38.0625C30.001 38.0625 30.8125 37.251 30.8125 36.25V9.73298L34.874 14.4712C35.5253 15.2313 36.6696 15.3193 37.4296 14.6678C38.1896 14.0164 38.2776 12.8721 37.6261 12.1121L30.3761 3.65378C30.0319 3.25204 29.529 3.02084 29 3.02084C28.471 3.02084 27.9681 3.25204 27.624 3.65378L20.3739 12.1121C19.7224 12.8721 19.8104 14.0164 20.5705 14.6678C21.3305 15.3193 22.4747 15.2313 23.1262 14.4712L27.1875 9.73298V36.25C27.1875 37.251 27.999 38.0625 29 38.0625Z"
                                                        fill="#F9AA2A"
                                                    />
                                                </svg>
                                                <div style={{ 
                                                    fontSize: '16px', 
                                                    fontWeight: '500', 
                                                    color: '#333',
                                                    marginBottom: '8px'
                                                }}>
                                                    {documents?.length > 0
                                                        ? `${documents.length} ${t('FilesSelected')}`
                                                        : t('DragDropFilesOrClick')}
                                                </div>
                                                {documents?.length > 0 && (
                                                    <div style={{ 
                                                        fontSize: '14px', 
                                                        color: '#666',
                                                        maxWidth: '300px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {documents.map((doc, index) => 
                                                            index < 3 ? doc.name : null
                                                        ).filter(Boolean).join(', ')}
                                                        {documents.length > 3 && ` +${documents.length - 3} more`}
                                                    </div>
                                                )}
                                            </label>
                                        )}
                                    </DropZoneContainer>
                                </div>

                                {/* Display selected files with a remove button */}
                                {documents.length > 0 && (
                                    <FileList>
                                        {documents.map((doc, index) => (
                                            <FileItem key={index}>
                                                <span>{doc.name}</span>
                                                <IconButton onClick={() => removeFile(index)} size="small">
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </FileItem>
                                        ))}
                                        <Button
                                            onClick={clearSelectedFiles}
                                            color="secondary"
                                            size="small"
                                            startIcon={<ClearIcon />}
                                        >
                                            {t('ClearAll')}
                                        </Button>
                                    </FileList>
                                )}
                            </div>

                        </div>

                        <div className={styles.footer}>
                            <button
                                type="button"
                                className={styles.cancel}
                                onClick={closeCreateModal}
                            >
                                {t('close')}
                            </button>
                            <button
                                type="button"
                                className={`${styles.save}${!allFieldsFilled ? ` ${styles.saveDisabled}` : ''}`}
                                disabled={!allFieldsFilled}
                                onClick={() => {
                                    if (!allFieldsFilled) return;
                                    const isManual = (formData.exchangeRateType ?? Constants.ExchangeRateType.Representative) === Constants.ExchangeRateType.Manual;
                                    const hasValidManualRate = manualRateInput !== '' && !isNaN(Number(manualRateInput)) && Number(manualRateInput) > 0;
                                    const state = getManualRateDisplayState();
                                    const effectiveRate = hasValidManualRate ? manualRateInput : (isManual ? state.calculatedManualRate : null);
                                    const merged = (isManual && effectiveRate)
                                        ? { ...formData, exchangeRate: effectiveRate }
                                        : formData;
                                    handleSave(merged);
                                }}
                            >
                                {t('save')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Add Account Card Modal */}
                <AddAccountCardModal
                    accountCardOptions={accountCardOptions}
                    open={editedAddAccountCard}
                    onClose={() => setEditedAddAccountCard(false)}
                    editedItem={newAccountCard}
                    isEdit={isEditAccountCard}
                    direction={direction}
                    client={client}
                    token={token}
                    setLoading={setLoading}
                    handleChangeAfterAdd={handleChangeAfterAdd}
                    fetchEnums={fetchEnums}
                    type={addAccountCardType}
                    palCodes={palCodes}
                    propPalCode={palCode}
                    setPalCode={setPalCode}
                />
            </div>
        </Modal>
        {projectModalOpen && (
            <Portal container={projectModalPortalContainer}>
                <ProjectModal
                    open={projectModalOpen}
                    mode="create"
                    direction={direction}
                    formData={{ code: '', name: '' }}
                    existingProjects={segmentationProjects}
                    onClose={handleProjectModalClose}
                    onSave={handleProjectModalSave}
                    loading={projectCreateLoading}
                    zIndex={1400}
                />
            </Portal>
        )}
        </>
    );
};

export default CreateDocumentModal;
